// Shared resource valorization / treatment quantification — used by BOTH the Resources tab UI
// (ResourceQuantify) and the balance engine (computeKPIs), so the numbers are always consistent.
// The user diverts a % of an occupant-driven waste stream to a loop; we multiply
//   input = stream_generated × capture_eff × (%divert/100)
//   output = input × yield  (from the vetted, cited backend `conversion` block)
// and derive the implied equipment (units, space m², parasitic kWh/yr) from the DB capacities.

export const DEFAULT_DIVERT_PCT = 100;

// Dynamic valorization footprint (v6) — area required to host the equipment, scaling with the
// diverted daily throughput Q. Anchored on the smallest documented vendor unit so published demo
// numbers reproduce (see kb_v5._AREA_PARAMS). Returns m² (0 for zero-incremental loops).
export function valorizationArea(am, qPerDay) {
  if (!am || am.model === "zero") return 0;
  if (!(qPerDay > 0)) return 0;
  const Q = qPerDay * (am.basis_factor ?? 1);
  const SF = am.SF ?? 1;
  if (am.model === "power") {
    const Qs = am.Q_smallest || 1;
    return (am.A_min || 0) * Math.max(1, Math.pow(Q / Qs, am.b || 1)) * SF;
  }
  if (am.model === "linear") return Math.max(am.A_min || 0, Q / (am.loading || 1)) * SF;
  if (am.model === "modular") return Math.ceil(Q / (am.Q_unit || 1)) * (am.A_unit || 0);
  if (am.model === "geometric") return Math.max(am.A_min || 0, Q * (am.m2_per_unit || 0)) * SF;
  return 0;
}

// Annual generation of a waste stream for the current site context.
//   organic/greywater/blackwater → per-capita baseline × occupancy
//   rain → precip(mm) × catchment area(m²) × runoff ÷ 1000  (m³/yr)
export function streamGenerated(streamKey, streams, ctx = {}) {
  if (!streamKey) return null;
  if (streamKey === "rain") {
    const area = Number(ctx.catchmentAreaM2) || 0;
    const runoff = ctx.runoff != null ? ctx.runoff : 0.8;
    const m3 = ((Number(ctx.precipMmYr) || 0) / 1000) * area * runoff;
    return { qty: m3, unit: "m\u00b3/yr" };
  }
  const b = streams?.[streamKey];
  if (streamKey === "organic") {
    // Typology-aware feedstock: prefer the selected building typology's per-occupant rate
    // (ctx.organicKgPersonYr), falling back to the dataset baseline (EPA/UN US household, 73).
    const perCapita = ctx.organicKgPersonYr != null ? ctx.organicKgPersonYr : (b?.value || 0);
    return { qty: perCapita * (Number(ctx.occupancy) || 0), unit: "kg/yr" };
  }
  if (!b) return null;
  const qty = (b.value || 0) * (Number(ctx.occupancy) || 0);
  return { qty, unit: "m\u00b3/yr" };
}

// Quantify a single selected resource loop at a given divert %.
export function quantifyResource(res, pct, streams, ctx = {}) {
  const conv = res?.conversion;
  if (!conv || !conv.stream) return null;
  const gen = streamGenerated(conv.stream, streams, ctx);
  if (!gen) return null;
  const frac = Math.max(0, Math.min(100, Number(pct) || 0)) / 100;
  const capture = conv.capture_eff != null ? conv.capture_eff : 1;
  const input = gen.qty * capture * frac;            // stream fed to this loop, /yr
  const outputQty = input * (conv.yield || 0);        // valorized output, /yr

  const cap = conv.input_capacity;                    // per-unit daily throughput
  const inputPerDay = input / 365;
  const units = cap ? (input > 0 ? Math.max(1, Math.ceil(inputPerDay / cap)) : 0) : null;

  // Footprint is sized on the DIVERTED throughput (what you choose to send to this loop), matching
  // the v6 area methodology — NOT the post-capture amount, so areas aren't understated.
  const divertedPerDay = (gen.qty * frac) / 365;
  const am = conv.area_model;
  let spaceM2, areaIntensity = null, marginalArea = null;
  if (am) {
    spaceM2 = valorizationArea(am, divertedPerDay);
    const qBasis = divertedPerDay * (am.basis_factor ?? 1);
    areaIntensity = qBasis > 0 ? spaceM2 / qBasis : null;       // m² per unit of daily throughput
    const divertedNext = (gen.qty * Math.min(1, frac + 0.1)) / 365;
    marginalArea = Math.max(0, valorizationArea(am, divertedNext) - spaceM2);
  } else {
    spaceM2 = units != null && conv.space_per_unit_m2 != null ? units * conv.space_per_unit_m2 : null;
  }

  // Parasitic (running) energy — per-input where given, else per-unit-per-day × units × 365.
  // (The legacy input×energy_kWh_day was the "1 kWh/kg" bug.)
  let parasiticKwh = null;
  if (conv.parasitic_per_input != null) parasiticKwh = input * conv.parasitic_per_input;
  else if (conv.parasitic_energy != null && units) parasiticKwh = units * conv.parasitic_energy * 365;

  const waterL = conv.output_kind === "water" ? outputQty * 1000 : 0;   // m³ → L
  const energyKwh = conv.output_kind === "energy" ? outputQty : 0;
  const amendmentKg = conv.output_kind === "amendment" ? outputQty : 0;

  return {
    stream: conv.stream, generated: gen.qty, genUnit: gen.unit,
    input, inputUnit: gen.unit, capture, frac,
    outputQty, outputUnit: conv.output_unit, outputKind: conv.output_kind,
    units, spaceM2, areaIntensity, marginalArea, areaModel: am || null,
    parasiticKwh, waterL, energyKwh, amendmentKg,
  };
}
